import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Collapse,
  IconButton,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SECTIONS_ORDER, SECTION_LABELS } from '../services/admissionStreamService';
import { clearAdmissionStream, cancelAdmissionStream } from '../redux/streamingSlice';

function renderJsonValue(data, depth = 1) {
  if (data === null || data === undefined) return null;
  if (typeof data === 'string') return <Typography key="str" variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75, mb: 0.5 }}>{data}</Typography>;
  if (typeof data === 'number' || typeof data === 'boolean') return <Typography key="prim" variant="body2" sx={{ mb: 0.5 }}>{String(data)}</Typography>;
  if (Array.isArray(data)) return <Box>{data.map((item, i) => <Box key={i} sx={{ mb: 0.5 }}>{renderJsonValue(item, depth)}</Box>)}</Box>;
  if (typeof data === 'object') {
    return (
      <Box>
        {Object.entries(data).map(([k, v]) => (
          <Box key={k} sx={{ mb: depth === 1 ? 1.5 : 0.75 }}>
            <Typography variant={depth === 1 ? 'subtitle2' : 'body2'} fontWeight={600} sx={{ mb: 0.25, textTransform: 'capitalize' }}>
              {k.replace(/_/g, ' ')}
            </Typography>
            <Box sx={{ pl: depth > 1 ? 1.5 : 0 }}>{renderJsonValue(v, depth + 1)}</Box>
          </Box>
        ))}
      </Box>
    );
  }
  return <Typography variant="body2">{String(data)}</Typography>;
}

function tryParseJson(text) {
  let t = text.trim();
  const fence = t.match(/^```(?:json|js|javascript)?\s*([\s\S]*?)\s*```$/i);
  if (fence) t = fence[1].trim();
  try { return JSON.parse(t); } catch {}
  const match = t.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
  if (match) { try { return JSON.parse(match[0]); } catch {} }
  return null;
}

function deepParseStrings(value) {
  if (typeof value === 'string') { const inner = tryParseJson(value); return inner !== null ? deepParseStrings(inner) : value; }
  if (Array.isArray(value)) return value.map(deepParseStrings);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, deepParseStrings(v)]));
  return value;
}

function SectionContent({ text }) {
  if (!text) return null;
  const parsed = tryParseJson(text);
  if (parsed === null) {
    const displayText = text.replace(/^```(?:json|js|javascript)?\s*/i, '').replace(/\s*```\s*$/, '').trim();
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>{displayText}</Typography>;
  }
  const deep = deepParseStrings(parsed);
  if (deep && typeof deep === 'object' && !Array.isArray(deep)) {
    const keys = Object.keys(deep);
    if (keys.length === 1 && typeof deep[keys[0]] === 'string') {
      return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>{deep[keys[0]]}</Typography>;
    }
  }
  return renderJsonValue(deep);
}

const STREAMING_PULSE = {
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
  animation: 'pulse 1.4s ease-in-out infinite',
};

const CURSOR_BLINK = {
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  animation: 'blink 0.9s step-end infinite',
};

function SectionRow({ sectionKey, sectionData }) {
  const [expanded, setExpanded] = useState(false);

  const label = sectionData?.label || SECTION_LABELS[sectionKey] || sectionKey;
  const status = sectionData?.status || 'pending';
  const text = sectionData?.text || '';

  useEffect(() => {
    if (status === 'streaming' || status === 'done') {
      setExpanded(true);
    }
  }, [status]);

  const isActive = status === 'streaming';
  const isDone = status === 'done';
  const isError = status === 'error';
  const isPending = status === 'pending';

  return (
    <Box
      sx={{
        borderRadius: 1,
        mb: 0.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isActive
          ? 'primary.light'
          : isDone
          ? 'success.light'
          : isError
          ? 'error.light'
          : 'divider',
        bgcolor: isActive ? 'primary.50' : isDone ? 'success.50' : 'transparent',
        opacity: isPending ? 0.5 : 1,
        transition: 'all 0.3s ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: (isActive || isDone) && text ? 'pointer' : 'default',
        }}
        onClick={() => {
          if ((isActive || isDone) && text) setExpanded((v) => !v);
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {isActive && (
            <RadioButtonUncheckedIcon
              fontSize="small"
              sx={{ color: 'primary.main', ...STREAMING_PULSE }}
            />
          )}
          {isDone && (
            <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
          )}
          {isError && (
            <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
          )}
          {isPending && (
            <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: isActive ? 600 : 500,
            color: isPending ? 'text.disabled' : 'text.primary',
          }}
        >
          {label}
        </Typography>

        {isError && <Chip label="Failed" color="error" size="small" />}

        {(isActive || isDone) && text && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {(isActive || isDone) && text && (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 2,
              pb: 1.5,
              borderTop: '1px solid',
              borderColor: isActive ? 'primary.100' : 'success.100',
            }}
          >
            <Box sx={{ pt: 1.25, color: 'text.primary' }}>
              <SectionContent text={text} />
              {isActive && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '2px',
                    height: '1em',
                    bgcolor: 'primary.main',
                    ml: 0.25,
                    verticalAlign: 'text-bottom',
                    borderRadius: '1px',
                    ...CURSOR_BLINK,
                  }}
                />
              )}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default function AdmissionGenerationProgress({ admissionUuid, onDismiss }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Cancel in-flight stream if the component unmounts before generation finishes
  useEffect(() => {
    return () => { dispatch(cancelAdmissionStream(admissionUuid)); };
  }, [admissionUuid, dispatch]);

  const streamState = useSelector(
    (state) => state.streaming?.admissions?.[admissionUuid]
  );

  if (!streamState) return null;

  const { status, sections, error } = streamState;

  const completedCount = Object.values(sections).filter((s) => s.status === 'done').length;
  const totalSections = SECTIONS_ORDER.length;
  const progress = Math.round((completedCount / totalSections) * 100);

  const statusLabel = {
    connecting: 'Connecting…',
    streaming: `${completedCount} of ${totalSections} sections complete`,
    completed: 'All sections generated',
    failed: 'Generation failed',
  }[status] || 'Working…';

  const handleDismiss = () => {
    dispatch(clearAdmissionStream({ uuid: admissionUuid }));
    onDismiss?.();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        borderColor: status === 'failed' ? 'error.light' : status === 'completed' ? 'success.light' : 'primary.light',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon
            fontSize="small"
            sx={{
              color: status === 'completed' ? 'success.main' : 'primary.main',
              ...(status === 'streaming' || status === 'connecting' ? STREAMING_PULSE : {}),
            }}
          />
          <Typography variant="subtitle2" fontWeight={600}>
            Generating Medical Record
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {(status === 'completed' || status === 'failed') && (
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => navigate(`/dashboard/medcharts/${admissionUuid}`)}
            >
              View Record
            </Button>
          )}
          {(status === 'completed' || status === 'failed') && (
            <Chip label="Dismiss" size="small" onClick={handleDismiss} sx={{ cursor: 'pointer' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
          <LinearProgress
            variant={status === 'connecting' ? 'indeterminate' : 'determinate'}
            value={progress}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
            color={status === 'failed' ? 'error' : status === 'completed' ? 'success' : 'primary'}
          />
          {status !== 'connecting' && (
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
              {progress}%
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {statusLabel}
        </Typography>
      </Box>

      {error && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="error" onClose={handleDismiss}>
            {error}
          </Alert>
        </Box>
      )}

      <Divider />

      <Box sx={{ p: 1.5 }}>
        {SECTIONS_ORDER.map((sectionKey) => (
          <SectionRow
            key={sectionKey}
            sectionKey={sectionKey}
            sectionData={sections[sectionKey]}
          />
        ))}
      </Box>
    </Paper>
  );
}
